import { createContext, useContext, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { api, throwIfError, getAuthConfig } from "@/api/client";
import type { components } from "@/api/generated/schema";

type MeResponse = components["schemas"]["MeResponse"];
type Permissions = components["schemas"]["Permissions"];
export type Action = keyof Permissions;

interface AuthContextValue {
  user: MeResponse;
  isLoading: false;
  can: (action: Action) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const result = await api.GET("/api/auth/me");
      if (result.response.status === 401) {
        Sentry.setUser(null);
        const { loginUrl } = await getAuthConfig();
        const redirect = encodeURIComponent(window.location.href);
        window.location.href = `${loginUrl}&redirect=${redirect}`;
        return new Promise<never>(() => {}); // never resolves — page is redirecting
      }
      const data = throwIfError(result, "Failed to fetch user");
      Sentry.setUser({ id: data.sub, email: data.email, username: data.name });
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });

  const permissions = user?.permissions;
  const can = useCallback(
    (action: Action) => permissions?.[action] ?? false,
    [permissions],
  );

  if (isLoading || !user) return null;

  return (
    <AuthContext.Provider value={{ user, isLoading: false, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
